import { Component, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { Directionality } from '@angular/cdk/bidi';
import { RbacService } from '@core/rbac/rbac.service';
import { AuthService } from '@core/auth/auth.service';
import { CompanySessionService } from '@core/auth/company-session.service';
import { OnboardingProgressService } from '@features/recruitment/dashboard/onboarding/onboarding-progress.service';
import { NavigationService } from '@core/navigation/navigation.service';
import { NotificationService } from '@core/notification/notification.service';
import { createSidebarVisibilityMap } from './sidebar-visibility';
import { SidebarCompanyComponent } from './sidebar-company/sidebar-company.component';
import { LoadingOverlayComponent } from '@shared/components/loading-overlay/loading-overlay.component';
import { ThemeService } from '@core/theme/theme.service';
import { UiService } from '@core/ui/ui.service';
import {
  SidebarConfig,
  SidebarExpandMode,
  SidebarItem,
  SidebarItemView,
  SidebarSectionView,
} from '@typings/app/sidebar.types';
import { switchLanguage, AppLanguage } from '@core/i18n/i18n.config';
import { LOCAL_STORAGE } from '@core/storage/storage.tokens';
import { LOCAL_KEYS } from '@core/storage/storage.keys';
import { environment } from '@env';
import { filter, map } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    MatIconModule,
    MatMenuModule,
    MatDivider,
    SidebarCompanyComponent,
    LoadingOverlayComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  isDev = environment.isDev;

  private rbac = inject(RbacService);
  private auth = inject(AuthService);
  private theme = inject(ThemeService);
  private translate = inject(TranslateService);
  private doc = inject(DOCUMENT);
  private router = inject(Router);
  private storage = inject(LOCAL_STORAGE);
  private directionality = inject(Directionality);
  ui = inject(UiService);
  private session = inject(CompanySessionService);
  private navigation = inject(NavigationService);
  private notification = inject(NotificationService);
  private onboardingProgress = inject(OnboardingProgressService);
  private visibilityMap = createSidebarVisibilityMap(this.session, this.onboardingProgress);

  activeCompany = this.session.activeCompany;
  memberships = this.session.memberships;

  /** True while a company switch is in flight — drives the full-page loading overlay. */
  switching = signal(false);

  protected readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  expandMode = linkedSignal<SidebarExpandMode>(() => 'hover');
  isExpanded = linkedSignal(() => this.expandMode() === 'always-expanded');
  currentLang = this.storage.watch(LOCAL_KEYS.language);

  open = input(false);
  closed = output<void>();

  config = input.required<SidebarConfig>();

  filteredSections = computed<SidebarSectionView[]>(() => {
    const url = this.currentUrl();
    return this.config()
      .sections.map((section) => ({
        titleKey: section.titleKey,
        items: section.items
          .filter((item) => {
            const permissionOk = !item.permission || this.rbac.hasPermission(item.permission)();
            const visibilityOk = !item.visibleWhen || this.visibilityMap[item.visibleWhen]();
            return permissionOk && visibilityOk;
          })
          .map((item) => ({ ...item, active: this.matchesUrl(url, item) })),
      }))
      .filter((section) => section.items.length > 0);
  });

  filteredBottomItems = computed<SidebarItemView[]>(() => {
    const url = this.currentUrl();
    return this.config()
      .bottomItems.filter((item) => !item.permission || this.rbac.hasPermission(item.permission)())
      .map((item) => ({ ...item, active: this.matchesUrl(url, item) }));
  });

  userName = computed(() => this.auth.employer()?.name ?? '');

  themeIcon = computed(() =>
    this.theme.theme() === 'dark'
      ? { fontSet: 'uicon-rr', fontIcon: 'fi-rr-sun' }
      : { fontSet: 'uicon-rr', fontIcon: 'fi-rr-moon' },
  );

  themeLabelKey = computed(() =>
    this.theme.theme() === 'dark' ? 'sidebar.switch_to_light' : 'sidebar.switch_to_dark',
  );

  userInitial = computed(() => {
    const name = this.auth.employer()?.name ?? '';
    return name.charAt(0).toUpperCase() || '?';
  });

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.ui.isMobile() && this.open()) {
          this.closed.emit();
        }
      });
  }

  onMouseEnter(): void {
    if (this.expandMode() === 'hover') {
      this.isExpanded.set(true);
    }
  }

  onMouseLeave(): void {
    if (this.expandMode() === 'hover') {
      this.isExpanded.set(false);
    }
  }

  close(): void {
    this.closed.emit();
  }

  switchLang(lang: AppLanguage): void {
    switchLanguage(this.translate, this.doc, this.storage, this.directionality, lang);
    // currentLang updates automatically via the watch() signal
  }

  toggleTheme(): void {
    this.theme.toggle();
  }

  onLogout(): void {
    this.auth.logout();
  }

  /**
   * Switch the active company, then hard-reload to the landing page. A full reload
   * (rather than an in-app route change) rebuilds all company-scoped state under the
   * new session token — otherwise already-loaded pages would keep stale data.
   */
  switchCompany(companyId: string): void {
    // Show the full-page loading overlay until the hard reload takes over. It stays
    // up on success (the reload replaces the page) and is only torn down on error.
    this.switching.set(true);
    this.session.selectCompany(companyId).subscribe({
      next: () => {
        const landing = this.router.serializeUrl(this.navigation.landingUrlTree());
        this.doc.location.assign(landing);
      },
      error: () => {
        this.switching.set(false);
        this.notification.error(this.translate.instant('auth.errors.company_select_failed'));
      },
    });
  }

  /** Pure URL matcher used by the section computeds to resolve each item's active state. */
  private matchesUrl(url: string, item: SidebarItem): boolean {
    const matches = [item.route, ...(item.activeMatchPaths ?? [])];
    return matches.some((p) => url === p || url.startsWith(`${p}/`) || url.startsWith(`${p}?`));
  }
}
